import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { login, logout, refreshSession, register } from "./api/authApi";
import {
  listListingReports,
  listMessageReports,
  listModerationFlags,
  updateListingReportStatus,
  updateMessageReportStatus,
  updateModerationFlagStatus,
} from "./api/adminApi";
import {
  createConversation,
  getConversation,
  listMyConversations,
  markConversationRead,
  reportMessage,
  sendConversationMessage,
} from "./api/conversationsApi";
import {
  analyzeListingImage,
  createListing,
  createListingWithImages,
  favoriteListing,
  getListing,
  getMyListing,
  listMyFavorites,
  listListings,
  listMyListings,
  markListingSold,
  publishListing,
  rateListing,
  createSellerReview,
  reportListing,
  ratingImprovements,
  unfavoriteListing,
  updateListing,
  uploadListingImage,
} from "./api/listingsApi";
import { searchListingsByImage } from "./api/searchApi";
import { createShopReview, followShop, getPublicShop, getShopFollowing, getShopReviews, listPublicShops, unfollowShop } from "./api/shopApi";
import { getPrivateUserProfile, getPublicUserProfile, getPublicUserReviews, updatePrivateUserProfile, uploadPrivateUserAvatar } from "./api/usersApi";
import TopBar from "./components/TopBar";
import { API_URL } from "./config/apiConfig";
import CreateListingPage from "./pages/CreateListingPage";
import FavoritesPage from "./pages/FavoritesPage";
import ListingDetailPage from "./pages/ListingDetailPage";
import LoginPage from "./pages/LoginPage";
import MessagesPage from "./pages/MessagesPage";
import AdminModerationPage from "./pages/AdminModerationPage";
import ProfilePage from "./pages/ProfilePage";
import RegisterPage from "./pages/RegisterPage";
import SalesPage from "./pages/SalesPage";
import MarketplacePage from "./pages/MarketplacePage";
import TradePage from "./pages/TradePage";
import TradeOffers from "./components/TradeOffers";
import SearchPage from "./pages/SearchPage";
import SellerProfilePage from "./pages/SellerProfilePage";
import ShopDashboardContainer from "./features/shop/ShopDashboardContainer";
import PublicShopPage from "./pages/PublicShopPage";
import { marketplaceNavPage, marketplaceTabFromHash, filterMarketplaceListings, hashForListing, hashForPage, hashForSeller, hashForShop, listingIdFromHash, pageFromHash, sellerIdFromHash, shopIdFromHash } from "./data/navigation";
import { mediaUrl, toListingCard } from "./utils/listingPresentation";
import "./App.css";

function App() {
  const [page, setPage] = useState(() => pageFromHash(window.location.hash));
  const [marketplaceTab, setMarketplaceTab] = useState(() => marketplaceTabFromHash(window.location.hash));
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [activeCategory, setActiveCategory] = useState("Alle");
  const [matches, setMatches] = useState([]);
  const [browseListings, setBrowseListings] = useState([]);
  const [isLoadingBrowseListings, setIsLoadingBrowseListings] = useState(false);
  const [browseListingsError, setBrowseListingsError] = useState("");
  const [listingSearchQuery, setListingSearchQuery] = useState("");
  const [shopSearchResults, setShopSearchResults] = useState([]);
  const [isLoadingShopSearch, setIsLoadingShopSearch] = useState(false);
  const [shopSearchError, setShopSearchError] = useState("");
  const [publicShop, setPublicShop] = useState(null);
  const [publicShopError, setPublicShopError] = useState("");
  const [isLoadingPublicShop, setIsLoadingPublicShop] = useState(false);
  const [publicShopReviews, setPublicShopReviews] = useState([]);
  const [publicShopFollowing, setPublicShopFollowing] = useState(false);
  const [publicShopActionError, setPublicShopActionError] = useState("");
  const [isUpdatingPublicShop, setIsUpdatingPublicShop] = useState(false);
  const [status, setStatus] = useState("Klar til billedsøgning.");
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [auth, setAuth] = useState(null);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [registerDisplayName, setRegisterDisplayName] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [registerError, setRegisterError] = useState("");
  const [isRegistering, setIsRegistering] = useState(false);
  const [authNotice, setAuthNotice] = useState("");
  const [myListings, setMyListings] = useState([]);
  const [isLoadingMyListings, setIsLoadingMyListings] = useState(false);
  const [myListingsError, setMyListingsError] = useState("");
  const [activeSalesStatus, setActiveSalesStatus] = useState("active");
  const [createListingForm, setCreateListingForm] = useState({
    title: "",
    description: "",
    category: "",
    condition: "",
    tradeMode: "sale",
    tradeWishes: "",
    priceAmount: "",
    location: "",
  });
  const [createListingImage, setCreateListingImage] = useState([]);
  const [createListingPreviewUrls, setCreateListingPreviewUrls] = useState([]);
  const [createListingError, setCreateListingError] = useState("");
  const [isCreatingListing, setIsCreatingListing] = useState(false);
  const [isCreatingDraftFromImage, setIsCreatingDraftFromImage] = useState(false);
  const [editListingConditionHints, setEditListingConditionHints] = useState([]);
  const [editListingAiConfidence, setEditListingAiConfidence] = useState(null);
  const [editListingFieldConfidences, setEditListingFieldConfidences] = useState({});
  const [editListingAiSuggestions, setEditListingAiSuggestions] = useState([]);
  const [editListingMissingInformation, setEditListingMissingInformation] = useState([]);
  const [editListingQualityWarnings, setEditListingQualityWarnings] = useState([]);
  const [editListingTags, setEditListingTags] = useState([]);
  const [editListingNeedsManualReview, setEditListingNeedsManualReview] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [editListingForm, setEditListingForm] = useState({
    title: "",
    description: "",
    category: "",
    condition: "",
    tradeMode: "sale",
    tradeWishes: "",
    priceAmount: "",
    location: "",
  });
  const [editListingImage, setEditListingImage] = useState([]);
  const [editListingPreviewUrls, setEditListingPreviewUrls] = useState([]);
  const [editListingError, setEditListingError] = useState("");
  const [isEditingListing, setIsEditingListing] = useState(false);
  const [salesActionError, setSalesActionError] = useState("");
  const [salesActionListingId, setSalesActionListingId] = useState("");
  const [tradeTarget, setTradeTarget] = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);
  const [isLoadingListingDetail, setIsLoadingListingDetail] = useState(false);
  const [listingDetailError, setListingDetailError] = useState("");
  const [detailReturnPage, setDetailReturnPage] = useState("search");
  const [selectedListingRating, setSelectedListingRating] = useState(null);
  const [isRatingListing, setIsRatingListing] = useState(false);
  const [ratingError, setRatingError] = useState("");
  const [selectedListingImprovements, setSelectedListingImprovements] = useState(null);
  const [dismissedImprovementKeys, setDismissedImprovementKeys] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState("");
  const [conversationDetail, setConversationDetail] = useState(null);
  const [messagesError, setMessagesError] = useState("");
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [isLoadingConversationDetail, setIsLoadingConversationDetail] = useState(false);
  const [messageDraft, setMessageDraft] = useState("");
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [messageReportNotice, setMessageReportNotice] = useState("");
  const [messageReportError, setMessageReportError] = useState("");
  const [reportingMessageId, setReportingMessageId] = useState("");
  const [sellerReviewRating, setSellerReviewRating] = useState(5);
  const [sellerReviewComment, setSellerReviewComment] = useState("");
  const [sellerReviewNotice, setSellerReviewNotice] = useState("");
  const [sellerReviewError, setSellerReviewError] = useState("");
  const [isSubmittingSellerReview, setIsSubmittingSellerReview] = useState(false);
  const [contactSellerError, setContactSellerError] = useState("");
  const [contactSellerListingId, setContactSellerListingId] = useState("");
  const [listingReportNotice, setListingReportNotice] = useState("");
  const [listingReportError, setListingReportError] = useState("");
  const [reportingListingId, setReportingListingId] = useState("");
  const [favoriteListings, setFavoriteListings] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [favoritesError, setFavoritesError] = useState("");
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(false);
  const [favoriteActionListingId, setFavoriteActionListingId] = useState("");
  const [sellerProfile, setSellerProfile] = useState(null);
  const [sellerProfileError, setSellerProfileError] = useState("");
  const [isLoadingSellerProfile, setIsLoadingSellerProfile] = useState(false);
  const [sellerProfileReturnPage, setSellerProfileReturnPage] = useState("search");
  const [publicShopReturnPage, setPublicShopReturnPage] = useState("search");
  const [privateProfile, setPrivateProfile] = useState(null);
  const [privateProfileError, setPrivateProfileError] = useState("");
  const [isLoadingPrivateProfile, setIsLoadingPrivateProfile] = useState(false);
  const [isSavingPrivateProfile, setIsSavingPrivateProfile] = useState(false);
  const [privateProfileNotice, setPrivateProfileNotice] = useState("");
  const [moderationFlags, setModerationFlags] = useState([]);
  const [moderationListingReports, setModerationListingReports] = useState([]);
  const [moderationMessageReports, setModerationMessageReports] = useState([]);
  const [moderationFlagsError, setModerationFlagsError] = useState("");
  const [isLoadingModerationFlags, setIsLoadingModerationFlags] = useState(false);
  const [moderationStatusFilter, setModerationStatusFilter] = useState("open");
  const [moderationTargetTypeFilter, setModerationTargetTypeFilter] = useState("all");
  const [updatingModerationItemKey, setUpdatingModerationItemKey] = useState("");
  const selectedListingIdRef = useRef("");

  function setRoutedPage(nextPage, options = {}) {
    const nextHash = hashForPage(nextPage);
    setPage(pageFromHash(nextHash));
    if (pageFromHash(nextHash) === "search") setMarketplaceTab(marketplaceTabFromHash(nextHash));
    if (window.location.hash === nextHash) {
      return;
    }
    if (options.replace) {
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.search}${nextHash}`
      );
      return;
    }
    window.location.hash = nextHash;
  }

  function authErrorMessage(error) {
    if (error.code === "ECONNABORTED") {
      return "Login tog for lang tid. Tjek at backend og databasen kører.";
    }
    if (!error.response) {
      return "Kunne ikke forbinde til backend. Tjek at API'en kører.";
    }
    if (error.response.status === 401) {
      return "Email eller adgangskode er forkert.";
    }
    return error.response?.data?.detail || error.message;
  }

  useEffect(() => {
    const handleHashChange = () => {
      setPage(pageFromHash(window.location.hash));
      setMarketplaceTab(marketplaceTabFromHash(window.location.hash));
    };

    if (!window.location.hash) {
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.search}${hashForPage("search")}`
      );
    }
    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  useEffect(() => {
    if (page !== "listing-detail") return;
    const listingId = listingIdFromHash(window.location.hash);
    if (!listingId || selectedListing?.id === listingId) return;

    let ignore = false;
    selectedListingIdRef.current = listingId;
    setSelectedListing(null);
    setListingDetailError("");
    setIsLoadingListingDetail(true);
    getListing({ apiUrl: API_URL, listingId })
      .then((listing) => {
        if (!ignore) {
          setSelectedListing(toListingCard(listing, {
            currentUserId: auth?.user?.id,
            favoriteIds,
          }));
        }
      })
      .catch((error) => {
        if (!ignore) {
          setListingDetailError(error.response?.status === 404 ? "Annoncen findes ikke længere." : "Annoncen kunne ikke hentes.");
        }
      })
      .finally(() => {
        if (!ignore) setIsLoadingListingDetail(false);
      });

    return () => {
      ignore = true;
    };
  }, [auth?.user?.id, favoriteIds, page, selectedListing?.id]);

  useEffect(() => {
    if (page !== "seller-profile") return;
    const userId = sellerIdFromHash(window.location.hash);
    if (!userId || sellerProfile?.id === userId) return;

    let ignore = false;
    setSellerProfile(null);
    setSellerProfileError("");
    setIsLoadingSellerProfile(true);
    Promise.all([
      getPublicUserProfile({ apiUrl: API_URL, userId }),
      getPublicUserReviews({ apiUrl: API_URL, userId }),
    ])
      .then(([profile, reviews]) => {
        if (!ignore) {
          setSellerProfile({
            ...profile,
            shop_logo_url: mediaUrl(profile.shop_logo_url),
            active_listings: profile.active_listings.map((listing) =>
              toListingCard(listing, { currentUserId: auth?.user?.id, favoriteIds, seller: profile })
            ),
            reviews: reviews.reviews,
          });
        }
      })
      .catch((error) => {
        if (!ignore) setSellerProfileError(error.response?.data?.detail || error.message);
      })
      .finally(() => {
        if (!ignore) setIsLoadingSellerProfile(false);
      });

    return () => {
      ignore = true;
    };
  }, [auth?.user?.id, favoriteIds, page, sellerProfile?.id]);

  useEffect(() => {
    if (page !== "public-shop") return;
    const shopId = shopIdFromHash(window.location.hash);
    if (!shopId || publicShop?.id === shopId) return;
    let ignore = false;
    setPublicShop(null);
    setPublicShopError("");
    setIsLoadingPublicShop(true);
    Promise.all([
      getPublicShop({ apiUrl: API_URL, shopId }),
      getShopReviews({ apiUrl: API_URL, shopId }),
      auth?.access_token ? getShopFollowing({ accessToken: auth.access_token, apiUrl: API_URL, shopId }) : Promise.resolve(null),
    ])
      .then(([shop, reviews, following]) => {
        if (!ignore) {
          setPublicShop(shop);
          setPublicShopReviews(reviews.reviews || []);
          setPublicShopFollowing(Boolean(following?.is_following));
        }
      })
      .catch((error) => {
        if (!ignore) setPublicShopError(error.response?.data?.detail || error.message);
      })
      .finally(() => {
        if (!ignore) setIsLoadingPublicShop(false);
      });
    return () => { ignore = true; };
  }, [auth?.access_token, page, publicShop?.id]);

  useEffect(() => {
    let active = true;
    refreshSession({ apiUrl: API_URL })
      .then((session) => {
        if (active) setAuth(session);
      })
      .catch(() => {
        if (active) setAuth(null);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const request = error.config;
        if (error.response?.status !== 401 || request?._skipAuthRefresh || request?._authRetried) {
          return Promise.reject(error);
        }
        request._authRetried = true;
        try {
          const session = await refreshSession({ apiUrl: API_URL });
          setAuth(session);
          request.headers = request.headers || {};
          request.headers.Authorization = `Bearer ${session.access_token}`;
          return axios.request(request);
        } catch {
          setAuth(null);
          return Promise.reject(error);
        }
      },
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  const refreshMyListings = useCallback(async () => {
    if (!auth?.access_token) {
      return;
    }

    setIsLoadingMyListings(true);
    setMyListingsError("");

    try {
      const listings = await listMyListings({
        accessToken: auth.access_token,
        apiUrl: API_URL,
      });
      setMyListings(listings);
    } catch (error) {
      setMyListings([]);
      setMyListingsError(error.response?.data?.detail || error.message);
    } finally {
      setIsLoadingMyListings(false);
    }
  }, [auth]);

  useEffect(() => {
    if (
      !["sales", "shop", "shop-create", "shop-preview", "shop-settings", "shop-stock"].includes(page) ||
      !auth?.access_token
    ) {
      return;
    }

    refreshMyListings();
  }, [auth, page, refreshMyListings]);


  useEffect(() => {
    if (page !== "profile" || !auth?.access_token) {
      return;
    }

    setIsLoadingPrivateProfile(true);
    setPrivateProfileError("");
    getPrivateUserProfile({
      accessToken: auth.access_token,
      apiUrl: API_URL,
    })
      .then((profile) => {
        setPrivateProfile({ ...profile, avatar_url: mediaUrl(profile.avatar_url) });
      })
      .catch((error) => {
        setPrivateProfile(null);
        setPrivateProfileError(error.response?.data?.detail || error.message);
      })
      .finally(() => {
        setIsLoadingPrivateProfile(false);
      });
  }, [auth, page]);

  const refreshModerationFlags = useCallback(async () => {
    if (!auth?.access_token || !auth?.user?.is_admin) {
      setModerationFlags([]);
      setModerationListingReports([]);
      setModerationMessageReports([]);
      return [];
    }

    setIsLoadingModerationFlags(true);
    setModerationFlagsError("");

    try {
      const [flags, listingReports, messageReports] = await Promise.all([
        listModerationFlags({
          accessToken: auth.access_token,
          apiUrl: API_URL,
          status: moderationStatusFilter,
          targetType: moderationTargetTypeFilter === "all" ? "" : moderationTargetTypeFilter,
        }),
        listListingReports({
          accessToken: auth.access_token,
          apiUrl: API_URL,
          status: moderationStatusFilter,
        }),
        listMessageReports({
          accessToken: auth.access_token,
          apiUrl: API_URL,
          status: moderationStatusFilter,
        }),
      ]);
      setModerationFlags(flags);
      setModerationListingReports(listingReports);
      setModerationMessageReports(messageReports);
      return flags;
    } catch (error) {
      setModerationFlags([]);
      setModerationListingReports([]);
      setModerationMessageReports([]);
      setModerationFlagsError(error.response?.data?.detail || error.message);
      return [];
    } finally {
      setIsLoadingModerationFlags(false);
    }
  }, [auth, moderationStatusFilter, moderationTargetTypeFilter]);

  useEffect(() => {
    if (page !== "admin-moderation") {
      return;
    }

    refreshModerationFlags();
  }, [page, refreshModerationFlags]);

  useEffect(() => {
    if (page !== "search" || !auth) {
      return;
    }

    let ignore = false;
    const timeoutId = window.setTimeout(() => {
      setIsLoadingBrowseListings(true);
      setBrowseListingsError("");
      listListings({ apiUrl: API_URL, query: listingSearchQuery })
        .then((listings) => {
          if (!ignore) setBrowseListings(listings);
        })
        .catch((error) => {
          if (!ignore) {
            setBrowseListings([]);
            setBrowseListingsError(error.response?.data?.detail || error.message);
          }
        })
        .finally(() => {
          if (!ignore) setIsLoadingBrowseListings(false);
        });
    }, 250);

    return () => {
      ignore = true;
      window.clearTimeout(timeoutId);
    };
  }, [auth, listingSearchQuery, page]);

  useEffect(() => {
    if (page !== "search" || !auth || (!listingSearchQuery.trim() && marketplaceTab !== "shops")) {
      setShopSearchResults([]);
      setShopSearchError("");
      return undefined;
    }

    let ignore = false;
    const timeoutId = window.setTimeout(() => {
      setIsLoadingShopSearch(true);
      setShopSearchError("");
      listPublicShops({ apiUrl: API_URL, query: listingSearchQuery })
        .then((shops) => {
          if (!ignore) setShopSearchResults(shops);
        })
        .catch((error) => {
          if (!ignore) {
            setShopSearchResults([]);
            setShopSearchError(error.response?.data?.detail || error.message);
          }
        })
        .finally(() => {
          if (!ignore) setIsLoadingShopSearch(false);
        });
    }, 250);

    return () => {
      ignore = true;
      window.clearTimeout(timeoutId);
    };
  }, [auth, listingSearchQuery, page, marketplaceTab]);

  const refreshFavorites = useCallback(async () => {
    if (!auth?.access_token) {
      setFavoriteListings([]);
      setFavoriteIds(new Set());
      return [];
    }

    setIsLoadingFavorites(true);
    setFavoritesError("");

    try {
      const favorites = await listMyFavorites({
        accessToken: auth.access_token,
        apiUrl: API_URL,
      });
      const ids = new Set(favorites.map((favorite) => favorite.listing.id));
      setFavoriteIds(ids);
      setFavoriteListings(
        favorites.map((favorite) => {
          const listing = favorite.listing;
          return {
          id: listing.id,
          title: listing.title,
          category: listing.category,
          location: listing.location || "",
          price: `${listing.price_amount} ${listing.price_currency}`,
          image: mediaUrl(listing.primary_image_url),
          images: (listing.image_urls || [listing.primary_image_url]).filter(Boolean).map(mediaUrl),
          description: listing.description,
          condition: listing.condition,
          status: listing.status || "active",
          inventoryCount: listing.inventory_count,
          aiScore: listing.ai_score,
          ownerId: listing.owner_id,
          seller: listing.seller_shop_name || listing.seller_display_name || listing.seller,
          sellerRole: listing.seller_role || "user",
          sellerLogo: mediaUrl(listing.seller_shop_logo_url),
          ratingAverage: listing.seller_rating_average || 0,
          ratingCount: listing.seller_rating_count || 0,
          verified: listing.seller_verified,
          isFavorite: true,
          isOwner: listing.owner_id === auth?.user?.id,
          favoriteId: favorite.id,
        };
        })
      );
      return favorites;
    } catch (error) {
      setFavoritesError(error.response?.data?.detail || error.message);
      return [];
    } finally {
      setIsLoadingFavorites(false);
    }
  }, [auth]);

  async function handleSavePrivateProfile(profile) {
    if (!auth?.access_token) {
      return false;
    }
    setIsSavingPrivateProfile(true);
    setPrivateProfileError("");
    setPrivateProfileNotice("");
    try {
      const updated = await updatePrivateUserProfile({
        accessToken: auth.access_token,
        apiUrl: API_URL,
        profile,
      });
      const displayedProfile = { ...updated, avatar_url: mediaUrl(updated.avatar_url) };
      setPrivateProfile(displayedProfile);
      const updatedAuth = { ...auth, user: { ...auth.user, ...updated } };
      setAuth(updatedAuth);
      setPrivateProfileNotice("Din profil er gemt.");
      return true;
    } catch (error) {
      setPrivateProfileError(error.response?.data?.detail || error.message);
      return false;
    } finally {
      setIsSavingPrivateProfile(false);
    }
  }

  async function handleUploadPrivateAvatar(avatar) {
    if (!auth?.access_token || !avatar) {
      return false;
    }
    setIsSavingPrivateProfile(true);
    setPrivateProfileError("");
    setPrivateProfileNotice("");
    try {
      const updated = await uploadPrivateUserAvatar({
        accessToken: auth.access_token,
        apiUrl: API_URL,
        avatar,
      });
      setPrivateProfile({ ...updated, avatar_url: mediaUrl(updated.avatar_url) });
      const updatedAuth = { ...auth, user: { ...auth.user, ...updated } };
      setAuth(updatedAuth);
      setPrivateProfileNotice("Dit profilbillede er gemt.");
      return true;
    } catch (error) {
      setPrivateProfileError(error.response?.data?.detail || error.message);
      return false;
    } finally {
      setIsSavingPrivateProfile(false);
    }
  }

  useEffect(() => {
    if (!auth?.access_token) {
      return;
    }

    refreshFavorites();
  }, [auth, page, refreshFavorites]);

  const refreshConversations = useCallback(async () => {
    if (!auth?.access_token) {
      return [];
    }

    setIsLoadingConversations(true);
    setMessagesError("");

    try {
      const inbox = await listMyConversations({
        accessToken: auth.access_token,
        apiUrl: API_URL,
      });
      const normalizedInbox = inbox.map((conversation) => ({
        ...conversation,
        listing: {
          ...conversation.listing,
          primary_image_url: mediaUrl(conversation.listing.primary_image_url),
        },
      }));
      setConversations(normalizedInbox);
      if (normalizedInbox.length === 0) {
        setSelectedConversationId("");
        setConversationDetail(null);
      } else if (!selectedConversationId) {
        setSelectedConversationId(normalizedInbox[0].id);
      } else if (!normalizedInbox.some((conversation) => conversation.id === selectedConversationId)) {
        setSelectedConversationId(normalizedInbox[0].id);
      }
      return normalizedInbox;
    } catch (error) {
      setConversations([]);
      setMessagesError(error.response?.data?.detail || error.message);
      return [];
    } finally {
      setIsLoadingConversations(false);
    }
  }, [auth, selectedConversationId]);

  const openConversation = useCallback(
    async (conversationId) => {
      if (!auth?.access_token || !conversationId) {
        return;
      }

      setSelectedConversationId(conversationId);
      setIsLoadingConversationDetail(true);
      setMessagesError("");

      try {
        const [detail] = await Promise.all([
          getConversation({
            accessToken: auth.access_token,
            apiUrl: API_URL,
            conversationId,
          }),
          markConversationRead({
            accessToken: auth.access_token,
            apiUrl: API_URL,
            conversationId,
          }),
        ]);
        setConversationDetail({
          ...detail,
          listing: {
            ...detail.listing,
            primary_image_url: mediaUrl(detail.listing.primary_image_url),
          },
        });
        setSellerReviewNotice("");
        setSellerReviewError("");
        await refreshConversations();
      } catch (error) {
        setConversationDetail(null);
        setMessagesError(error.response?.data?.detail || error.message);
      } finally {
        setIsLoadingConversationDetail(false);
      }
    },
    [auth, refreshConversations]
  );

  useEffect(() => {
    if (page !== "messages" || !auth?.access_token) {
      return;
    }

    refreshConversations();
  }, [auth, page, refreshConversations]);

  useEffect(() => {
    if (page !== "messages" || !auth?.access_token || !selectedConversationId) {
      return;
    }

    openConversation(selectedConversationId);
  }, [auth, openConversation, page, selectedConversationId]);

  const listings = useMemo(() => {
    const source = hasSearched ? matches : browseListings;
    let base = source.map((listing) => toListingCard(listing, {
      currentUserId: auth?.user?.id,
      favoriteIds,
      hasMatchScore: hasSearched,
    }));

    const normalizedQuery = listingSearchQuery.trim().toLocaleLowerCase("da-DK");
    if (normalizedQuery) {
      base = base.filter((listing) =>
        [listing.title, listing.seller]
          .filter(Boolean)
          .some((value) => value.toLocaleLowerCase("da-DK").includes(normalizedQuery))
      );
    }

    if (activeCategory === "Alle") {
      return base;
    }

    return base.filter((listing) => listing.categoryKey === activeCategory);
  }, [activeCategory, auth, browseListings, favoriteIds, hasSearched, listingSearchQuery, matches]);

  function listingCardFromPublicListing(listing, seller = {}) {
    return toListingCard(listing, {
      currentUserId: auth?.user?.id,
      favoriteIds,
      seller,
    });
  }

  function upsertMyListing(listing) {
    setMyListings((current) => {
      const existingIndex = current.findIndex((item) => item.id === listing.id);
      if (existingIndex === -1) {
        return [listing, ...current];
      }

      return current.map((item) => (item.id === listing.id ? { ...item, ...listing } : item));
    });
  }

  function removeMyListing(listingId) {
    setMyListings((current) => current.filter((listing) => listing.id !== listingId));
  }

  function handleFileChange(event) {
    const file = event.target.files?.[0];
    setSelectedFile(file || null);
    setMatches([]);
    setHasSearched(false);

    if (!file) {
      setPreviewUrl((current) => {
        if (current.startsWith("blob:")) URL.revokeObjectURL(current);
        return "";
      });
      setStatus("Klar til billedsøgning.");
      return;
    }

    setPreviewUrl((current) => {
      if (current.startsWith("blob:")) URL.revokeObjectURL(current);
      return URL.createObjectURL(file);
    });
    setStatus(`${file.name} er klar. Start søgningen, når du er klar.`);
  }

  function handleClearSearch() {
    setSelectedFile(null);
    setPreviewUrl((current) => {
      if (current.startsWith("blob:")) URL.revokeObjectURL(current);
      return "";
    });
    setMatches([]);
    setHasSearched(false);
    setStatus("Klar til billedsøgning.");
  }

  async function handleSearch(event) {
    event.preventDefault();

    if (!selectedFile) {
      setStatus("Vælg et billede, før du søger.");
      return;
    }

    setIsSearching(true);
    setStatus("AI sammenligner billedet med dine annoncer...");

    try {
      const suggestions = await searchListingsByImage({
        accessToken: auth.access_token,
        apiUrl: API_URL,
        image: selectedFile,
      });
      setMatches(suggestions);
      setHasSearched(true);
      setStatus(`${suggestions.length} relevante annoncer fundet.`);
    } catch (error) {
      setMatches([]);
      setHasSearched(true);
      setStatus(error.response?.data?.detail || error.message);
    } finally {
      setIsSearching(false);
    }
  }

  async function handleLogin(event) {
    event.preventDefault();
    setLoginError("");
    setIsLoggingIn(true);

    try {
      const authResponse = await login({
        apiUrl: API_URL,
        email: loginEmail,
        password: loginPassword,
      });
      setAuth(authResponse);
      setAuthNotice("");
      setLoginPassword("");
      setRoutedPage("profile");
    } catch (error) {
      setAuth(null);
      setLoginError(authErrorMessage(error));
    } finally {
      setIsLoggingIn(false);
    }
  }

  async function handleRegister(event) {
    event.preventDefault();
    setRegisterError("");
    setIsRegistering(true);

    try {
      const authResponse = await register({
        apiUrl: API_URL,
        displayName: registerDisplayName,
        email: registerEmail,
        password: registerPassword,
        role: "user",
      });
      setAuth(authResponse);
      setAuthNotice("");
      setRegisterPassword("");
      setRoutedPage("profile");
    } catch (error) {
      setAuth(null);
      setRegisterError(authErrorMessage(error));
    } finally {
      setIsRegistering(false);
    }
  }

  async function handleLogout() {
    try {
      await logout({ apiUrl: API_URL });
    } catch {
      // Local logout should still clear browser state if the session is already invalid.
    }
    setAuth(null);
    setAuthNotice("");
    setConversations([]);
    setConversationDetail(null);
    setSelectedConversationId("");
    setMessageDraft("");
    setMessageReportNotice("");
    setMessageReportError("");
    setReportingMessageId("");
    setSellerReviewRating(5);
    setSellerReviewComment("");
    setSellerReviewNotice("");
    setSellerReviewError("");
    setIsSubmittingSellerReview(false);
    setListingReportNotice("");
    setListingReportError("");
    setReportingListingId("");
    setFavoriteListings([]);
    setFavoriteIds(new Set());
    setFavoritesError("");
    setRoutedPage("login", { replace: true });
  }

  function handleCreateListingChange(event) {
    const { name, value } = event.target;
    setCreateListingForm((current) => ({
      ...current,
      [name]: value,
    }));
  }

  function handleCreateListingImageChange(event) {
    const files = Array.from(event.target.files || []);
    setCreateListingImage((current) => [...current, ...files]);
    setCreateListingPreviewUrls((current) => [
      ...current,
      ...files.map((file) => URL.createObjectURL(file)),
    ]);
    event.target.value = "";
    setEditListingConditionHints([]);
    setEditListingAiConfidence(null);
    setEditListingFieldConfidences({});
    setEditListingAiSuggestions([]);
    setEditListingMissingInformation([]);
    setEditListingQualityWarnings([]);
    setEditListingTags([]);
    setEditListingNeedsManualReview(false);
  }

  function handleRemoveCreateListingImage(index) {
    setCreateListingImage((current) => current.filter((_, imageIndex) => imageIndex !== index));
    setCreateListingPreviewUrls((current) =>
      current.filter((_, imageIndex) => imageIndex !== index),
    );
  }

  function resetCreateListingForm() {
    setCreateListingForm({
      title: "",
      description: "",
      category: "",
      condition: "",
      tradeMode: "sale",
      tradeWishes: "",
      priceAmount: "",
      location: "",
    });
    setCreateListingImage([]);
    setCreateListingPreviewUrls([]);
    setCreateListingError("");
    setEditListingConditionHints([]);
    setEditListingAiConfidence(null);
    setEditListingFieldConfidences({});
    setEditListingAiSuggestions([]);
    setEditListingMissingInformation([]);
    setEditListingQualityWarnings([]);
    setEditListingTags([]);
    setEditListingNeedsManualReview(false);
  }

  async function handleCreateListing(event) {
    event.preventDefault();
    setCreateListingError("");
    setIsCreatingListing(true);

    try {
      const listingPayload = {
        title: createListingForm.title,
        description: createListingForm.description,
        category: createListingForm.category,
        condition: createListingForm.condition || null,
        trade_mode: createListingForm.tradeMode,
        trade_wishes: createListingForm.tradeWishes || null,
        price_amount: Number(createListingForm.priceAmount),
        price_currency: "DKK",
        location: createListingForm.location || null,
      };
      const listing = createListingImage.length
        ? await createListingWithImages({
            accessToken: auth.access_token,
            apiUrl: API_URL,
            images: createListingImage,
            listing: listingPayload,
          })
        : await createListing({
        accessToken: auth.access_token,
        apiUrl: API_URL,
            listing: listingPayload,
          });
      upsertMyListing(listing);

      resetCreateListingForm();
      setActiveSalesStatus("active");
      await reviewSavedListing(listing);
    } catch (error) {
      setCreateListingError(error.response?.data?.detail || error.message);
    } finally {
      setIsCreatingListing(false);
    }
  }

  async function handleCreateDraftFromImage() {
    setCreateListingError("");

    if (!createListingImage.length) {
      setCreateListingError("Vælg et billede, før AI udfylder annoncen.");
      return;
    }

    setIsCreatingDraftFromImage(true);
    try {
      const analysis = await analyzeListingImage({
        accessToken: auth.access_token,
        apiUrl: API_URL,
        images: createListingImage.slice(0, 3),
      });
      setCreateListingForm((current) => ({
        ...current,
        title: analysis.title || current.title,
        description: analysis.description || current.description,
        category: analysis.category || current.category,
      }));
      setEditListingConditionHints(analysis.condition_hints || []);
      setEditListingAiConfidence(analysis.analysis_confidence ?? null);
      setEditListingFieldConfidences(analysis.field_confidences || {});
      setEditListingAiSuggestions(analysis.suggestions || []);
      setEditListingMissingInformation(analysis.missing_information || []);
      setEditListingQualityWarnings(analysis.quality_warnings || []);
      setEditListingTags(analysis.tags || []);
      setEditListingNeedsManualReview(Boolean(analysis.needs_manual_review));
    } catch (error) {
      setCreateListingError(error.response?.data?.detail || error.message);
    } finally {
      setIsCreatingDraftFromImage(false);
    }
  }

  async function handleEditListing(listing) {
    setEditingListing(listing);
    setEditListingForm({
      title: listing.title || "",
      description: listing.description || "",
      category: listing.category || "",
      condition: listing.condition || "",
      tradeMode: listing.trade_mode || "sale",
      tradeWishes: listing.trade_wishes || "",
      priceAmount: String(listing.price_amount ?? ""),
      location: listing.location || "",
    });
    setEditListingImage([]);
    setEditListingPreviewUrls(
      (listing.image_urls || [listing.primary_image_url]).filter(Boolean).map(mediaUrl),
    );
    setEditListingError("");
    setEditListingConditionHints([]);
    setEditListingAiConfidence(null);
    setEditListingFieldConfidences({});
    setEditListingAiSuggestions([]);
    setEditListingMissingInformation([]);
    setEditListingQualityWarnings([]);
    setEditListingTags([]);
    setEditListingNeedsManualReview(false);
    handleSetPage("edit-listing");

    try {
      const detail = await getMyListing({
        accessToken: auth.access_token,
        apiUrl: API_URL,
        listingId: listing.id,
      });
      if (!detail.ai_draft) {
        setEditListingPreviewUrls(
          (detail.image_urls || [detail.primary_image_url || listing.primary_image_url])
            .filter(Boolean)
            .map(mediaUrl),
        );
        return;
      }

      setEditListingPreviewUrls(
        (detail.image_urls || [detail.primary_image_url || listing.primary_image_url])
          .filter(Boolean)
          .map(mediaUrl),
      );
      setEditListingConditionHints(detail.ai_draft.condition_hints || []);
      setEditListingAiConfidence(detail.ai_draft.analysis_confidence ?? null);
      setEditListingFieldConfidences(detail.ai_draft.field_confidences || {});
      setEditListingAiSuggestions(detail.ai_draft.suggestions || []);
      setEditListingMissingInformation(detail.ai_draft.missing_information || []);
      setEditListingQualityWarnings(detail.ai_draft.quality_warnings || []);
      setEditListingTags(detail.ai_draft.tags || []);
      setEditListingNeedsManualReview(Boolean(detail.ai_draft.needs_manual_review));
    } catch {
      // The editable draft still works if metadata cannot be loaded.
    }
  }

  function handleEditListingChange(event) {
    const { name, value } = event.target;
    setEditListingForm((current) => ({
      ...current,
      [name]: value,
    }));
  }

  function handleEditListingImageChange(event) {
    const files = Array.from(event.target.files || []);
    setEditListingImage((current) => [...current, ...files]);
    setEditListingPreviewUrls((current) => [
      ...(editListingImage.length ? current : []),
      ...files.map((file) => URL.createObjectURL(file)),
    ]);
    event.target.value = "";
  }

  function handleRemoveEditListingImage(index) {
    setEditListingImage((current) => current.filter((_, imageIndex) => imageIndex !== index));
    setEditListingPreviewUrls((current) =>
      current.filter((_, imageIndex) => imageIndex !== index),
    );
  }

  async function handleEditListingSubmit(event) {
    event.preventDefault();
    if (!editingListing) {
      return;
    }

    setEditListingError("");
    setIsEditingListing(true);

    try {
      const updatedListing = await updateListing({
        accessToken: auth.access_token,
        apiUrl: API_URL,
        listingId: editingListing.id,
        listing: {
          title: editListingForm.title,
          description: editListingForm.description,
          category: editListingForm.category,
          condition: editListingForm.condition || null,
          trade_mode: editListingForm.tradeMode,
          trade_wishes: editListingForm.tradeWishes || null,
          price_amount: Number(editListingForm.priceAmount),
          price_currency: "DKK",
          location: editListingForm.location || null,
        },
      });
      upsertMyListing(updatedListing);

      for (const image of editListingImage) {
        await uploadListingImage({
          accessToken: auth.access_token,
          apiUrl: API_URL,
          image,
          listingId: editingListing.id,
        });
      }

      setEditingListing(null);
      setEditListingImage([]);
      setEditListingPreviewUrls([]);
      setEditListingConditionHints([]);
      setEditListingAiConfidence(null);
      setEditListingFieldConfidences({});
      setEditListingAiSuggestions([]);
      setEditListingMissingInformation([]);
      setEditListingQualityWarnings([]);
      setEditListingTags([]);
      setActiveSalesStatus(updatedListing.status || editingListing.status || "draft");
      await reviewSavedListing(updatedListing);
    } catch (error) {
      setEditListingError(error.response?.data?.detail || error.message);
    } finally {
      setIsEditingListing(false);
    }
  }

  async function handlePublishListing(listing) {
    setSalesActionError("");
    setSalesActionListingId(listing.id);
    try {
      await publishListing({
        accessToken: auth.access_token,
        apiUrl: API_URL,
        listingId: listing.id,
      });
      setActiveSalesStatus("active");
      await refreshMyListings();
    } catch (error) {
      setSalesActionError(error.response?.data?.detail || error.message);
    } finally {
      setSalesActionListingId("");
    }
  }

  async function handleMarkSold(listing) {
    setSalesActionError("");
    setSalesActionListingId(listing.id);
    try {
      await markListingSold({
        accessToken: auth.access_token,
        apiUrl: API_URL,
        listingId: listing.id,
      });
      setActiveSalesStatus("sold");
      await refreshMyListings();
    } catch (error) {
      setSalesActionError(error.response?.data?.detail || error.message);
    } finally {
      setSalesActionListingId("");
    }
  }

  function handleOpenSearchListing(listing) {
    selectedListingIdRef.current = listing.id;
    setSelectedListing(listing);
    setSelectedListingRating(null);
    setRatingError("");
    setContactSellerError("");
    setContactSellerListingId("");
    setListingReportNotice("");
    setListingReportError("");
    setReportingListingId("");
    setSelectedListingImprovements(null);
    setDismissedImprovementKeys([]);
    setDetailReturnPage("search");
    window.location.hash = hashForListing(listing.id);
  }

  async function handleOpenSellerListing(listing) {
    selectedListingIdRef.current = listing.id;
    setSelectedListing(listing);
    setSelectedListingRating(null);
    setRatingError("");
    setContactSellerError("");
    setContactSellerListingId("");
    setListingReportNotice("");
    setListingReportError("");
    setReportingListingId("");
    setSelectedListingImprovements(null);
    setDismissedImprovementKeys([]);
    setDetailReturnPage("seller-profile");
    window.location.hash = hashForListing(listing.id);

    try {
      const detail = await getListing({
        apiUrl: API_URL,
        listingId: listing.id,
      });
      if (selectedListingIdRef.current !== listing.id) {
        return;
      }
      setSelectedListing(listingCardFromPublicListing(detail, {
        id: detail.owner_id,
        display_name: detail.seller_display_name,
        shop_name: detail.seller_shop_name,
        location: detail.location,
        rating_average: detail.seller_rating_average,
        rating_count: detail.seller_rating_count,
        seller_verified: detail.seller_verified,
      }));
    } catch {
      // The summary card still gives the buyer enough context if detail hydration fails.
    }
  }

  function handleOpenSellerProfile(listing) {
    const userId = listing?.ownerId || listing?.owner_id;
    if (!userId) {
      return;
    }

    setSellerProfileReturnPage(page === "seller-profile" ? "listing-detail" : page);
    window.location.hash = hashForSeller(userId);
  }

  function handleOpenPublicShop(shop) {
    if (!shop?.id) {
      return;
    }
    if (page !== "public-shop") {
      setPublicShopReturnPage(page);
    }
    window.location.hash = hashForShop(shop.id);
  }

  function handleBackFromPublicShop() {
    handleSetPage(publicShopReturnPage);
  }

  function handleOpenPublicShopListing(listing) {
    handleOpenSearchListing(toListingCard(listing, {
      currentUserId: auth?.user?.id,
      favoriteIds,
      seller: publicShop ? {
        id: publicShop.owner_id,
        display_name: publicShop.owner_display_name,
        shop_name: publicShop.shop_name,
        shop_logo_url: publicShop.shop_logo_url,
        rating_average: publicShop.rating_average,
        rating_count: publicShop.rating_count,
      } : undefined,
    }));
    setDetailReturnPage("public-shop");
  }

  async function handleTogglePublicShopFollow() {
    if (!auth?.access_token || !publicShop) return handleSetPage("login");
    setIsUpdatingPublicShop(true);
    setPublicShopActionError("");
    try {
      const result = publicShopFollowing
        ? await unfollowShop({ accessToken: auth.access_token, apiUrl: API_URL, shopId: publicShop.id })
        : await followShop({ accessToken: auth.access_token, apiUrl: API_URL, shopId: publicShop.id });
      setPublicShopFollowing(result.is_following);
      setPublicShop((current) => current ? { ...current, follower_count: result.follower_count } : current);
    } catch (error) {
      setPublicShopActionError(error.response?.data?.detail || error.message);
    } finally {
      setIsUpdatingPublicShop(false);
    }
  }

  async function handleCreatePublicShopReview(review) {
    if (!auth?.access_token || !publicShop) {
      handleSetPage("login");
      return false;
    }
    setIsUpdatingPublicShop(true);
    setPublicShopActionError("");
    try {
      const created = await createShopReview({ accessToken: auth.access_token, apiUrl: API_URL, review, shopId: publicShop.id });
      const reviews = [created, ...publicShopReviews];
      const average = reviews.reduce((sum, value) => sum + value.rating, 0) / reviews.length;
      setPublicShopReviews(reviews);
      setPublicShop((current) => current ? { ...current, rating_average: average, rating_count: reviews.length } : current);
      return true;
    } catch (error) {
      setPublicShopActionError(error.response?.data?.detail || error.message);
      return false;
    } finally {
      setIsUpdatingPublicShop(false);
    }
  }

  async function handleOpenSalesListing(listing, index) {
    selectedListingIdRef.current = listing.id;
    setSelectedListing({
      ...listing,
      aiScore: listing.ai_score,
      inventoryCount: listing.inventory_count,
      image: salesListingImage(listing, index),
      images: (listing.image_urls || [listing.primary_image_url]).filter(Boolean).map(mediaUrl),
      ownerId: auth?.user?.id,
      price: `${listing.price_amount} ${listing.price_currency}`,
      ratingAverage: auth?.user?.rating_average || 0,
      ratingCount: auth?.user?.rating_count || 0,
      seller: auth?.user?.display_name || "Dig",
      sellerRole: auth?.user?.role || "user",
      verified: auth?.user?.email_verified || false,
    });
    setSelectedListingRating(null);
    setRatingError("");
    setContactSellerError("");
    setContactSellerListingId("");
    setListingReportNotice("");
    setListingReportError("");
    setReportingListingId("");
    setSelectedListingImprovements(null);
    setDismissedImprovementKeys([]);
    setDetailReturnPage("sales");
    window.location.hash = hashForListing(listing.id);

    try {
      const detail = await getMyListing({
        accessToken: auth.access_token,
        apiUrl: API_URL,
        listingId: listing.id,
      });
      const improvements = ratingImprovements(detail.ai_rating);
      if (selectedListingIdRef.current !== listing.id) {
        return;
      }
      setSelectedListing((current) =>
        current?.id === listing.id
          ? {
              ...current,
              ...detail,
              aiScore: detail.ai_score,
              inventoryCount: detail.inventory_count,
              image: salesListingImage(detail, index),
              images: (detail.image_urls || [detail.primary_image_url]).filter(Boolean).map(mediaUrl),
            }
          : current
      );
      setSelectedListingRating(detail.ai_rating || null);
      setSelectedListingImprovements(improvements);
    } catch {
      // The detail page can still show the listing summary if hydration fails.
    }
  }

  function salesListingImage(listing, index) {
    return mediaUrl(listing.primary_image_url);
  }

  function handleBackFromDetail() {
    if (detailReturnPage === "seller-profile" && sellerProfile?.id) {
      window.location.hash = hashForSeller(sellerProfile.id);
      return;
    }
    if (detailReturnPage === "public-shop" && publicShop?.id) {
      window.location.hash = hashForShop(publicShop.id);
      return;
    }
    handleSetPage(detailReturnPage);
  }

  function handleBackFromSellerProfile() {
    handleSetPage(sellerProfileReturnPage);
  }

  function handleDetailEdit(listing) {
    const sourceListing = myListings.find((item) => item.id === listing.id) || listing;
    handleEditListing(sourceListing);
  }

  async function handleDetailPublish(listing) {
    await handlePublishListing(listing);
    setRoutedPage("sales");
  }

  async function handleDetailMarkSold(listing) {
    await handleMarkSold(listing);
    setRoutedPage("sales");
  }

  async function handleRateListing(listing) {
    if (!listing?.id) {
      return;
    }

    setRatingError("");
    setIsRatingListing(true);
    try {
      const rating = await rateListing({
        accessToken: auth.access_token,
        apiUrl: API_URL,
        listingId: listing.id,
      });
      if (selectedListingIdRef.current !== listing.id) return;
      const improvements = ratingImprovements(rating);
      setSelectedListingRating(rating);
      setSelectedListingImprovements(improvements);
      setDismissedImprovementKeys([]);
      setSelectedListing((current) =>
        current?.id === listing.id ? { ...current, aiScore: rating.score, ai_score: rating.score } : current
      );
      upsertMyListing({ id: listing.id, ai_score: rating.score });
      await refreshMyListings();
    } catch (error) {
      setRatingError(error.response?.data?.detail || error.message);
    } finally {
      setIsRatingListing(false);
    }
  }

  async function handleSendMessage(event) {
    event.preventDefault();
    if (!auth?.access_token || !selectedConversationId || !messageDraft.trim()) {
      return;
    }

    setIsSendingMessage(true);
    setMessagesError("");

    try {
      await sendConversationMessage({
        accessToken: auth.access_token,
        apiUrl: API_URL,
        body: messageDraft,
        conversationId: selectedConversationId,
      });
      setMessageDraft("");
      await openConversation(selectedConversationId);
    } catch (error) {
      setMessagesError(error.response?.data?.detail || error.message);
    } finally {
      setIsSendingMessage(false);
    }
  }

  async function handleReportMessage(message) {
    if (!auth?.access_token || !message?.id || message.sender?.id === auth?.user?.id) {
      return;
    }

    setReportingMessageId(message.id);
    setMessageReportNotice("");
    setMessageReportError("");

    try {
      await reportMessage({
        accessToken: auth.access_token,
        apiUrl: API_URL,
        messageId: message.id,
        reason: "suspicious_message",
        details: "Rapporteret fra beskedtråden i appen.",
      });
      setMessageReportNotice("Beskeden er rapporteret til moderation.");
    } catch (error) {
      setMessageReportError(error.response?.data?.detail || error.message);
    } finally {
      setReportingMessageId("");
    }
  }

  async function handleCreateSellerReview(event) {
    event.preventDefault();
    if (!auth?.access_token || !conversationDetail?.listing?.id) {
      return;
    }

    setIsSubmittingSellerReview(true);
    setSellerReviewNotice("");
    setSellerReviewError("");

    try {
      await createSellerReview({
        accessToken: auth.access_token,
        apiUrl: API_URL,
        listingId: conversationDetail.listing.id,
        rating: sellerReviewRating,
        comment: sellerReviewComment,
      });
      setSellerReviewRating(5);
      setSellerReviewComment("");
      setSellerReviewNotice("Din anmeldelse er gemt.");
    } catch (error) {
      setSellerReviewError(error.response?.data?.detail || error.message);
    } finally {
      setIsSubmittingSellerReview(false);
    }
  }

  async function handleReportListing(listing) {
    if (!auth?.access_token || !listing?.id || listing.isOwner) {
      return;
    }

    setReportingListingId(listing.id);
    setListingReportNotice("");
    setListingReportError("");

    try {
      await reportListing({
        accessToken: auth.access_token,
        apiUrl: API_URL,
        listingId: listing.id,
        reason: "suspicious_listing",
        details: "Rapporteret fra annoncevisningen i appen.",
      });
      setListingReportNotice("Annoncen er rapporteret til moderation.");
    } catch (error) {
      setListingReportError(error.response?.data?.detail || error.message);
    } finally {
      setReportingListingId("");
    }
  }

  async function handleToggleFavorite(listing) {
    if (!auth?.access_token || !listing?.id || listing.isOwner) {
      return;
    }

    setFavoriteActionListingId(listing.id);
    setFavoritesError("");

    try {
      if (favoriteIds.has(listing.id) || listing.isFavorite) {
        await unfavoriteListing({
          accessToken: auth.access_token,
          apiUrl: API_URL,
          listingId: listing.id,
        });
        setFavoriteIds((current) => {
          const next = new Set(current);
          next.delete(listing.id);
          return next;
        });
        setFavoriteListings((current) => current.filter((item) => item.id !== listing.id));
        setSelectedListing((current) =>
          current?.id === listing.id ? { ...current, isFavorite: false } : current
        );
      } else {
        await favoriteListing({
          accessToken: auth.access_token,
          apiUrl: API_URL,
          listingId: listing.id,
        });
        setFavoriteIds((current) => new Set([...current, listing.id]));
        setSelectedListing((current) =>
          current?.id === listing.id ? { ...current, isFavorite: true } : current
        );
      }
      await refreshFavorites();
    } catch (error) {
      setFavoritesError(error.response?.data?.detail || error.message);
    } finally {
      setFavoriteActionListingId("");
    }
  }

  function startTrade(listing) {
    if (!auth) return handleSetPage("login");
    setTradeTarget({ ...listing, primary_image_url: listing.primary_image_url || listing.image });
    handleSetPage("trades");
  }

  async function openTradeConversation(id) {
    setSelectedConversationId(id);
    setRoutedPage("messages");
    await openConversation(id);
  }

  function createTradeListing() {
    resetCreateListingForm();
    setCreateListingForm(current => ({ ...current, tradeMode: "trade" }));
    handleSetPage("create-listing");
  }

  async function handleContactSeller(listing) {
    if (!auth?.access_token || !listing?.id) {
      return;
    }

    setContactSellerError("");
    setContactSellerListingId(listing.id);

    try {
      const conversation = await createConversation({
        accessToken: auth.access_token,
        apiUrl: API_URL,
        listingId: listing.id,
      });
      setSelectedConversationId(conversation.id);
      setRoutedPage("messages");
      await openConversation(conversation.id);
    } catch (error) {
      setContactSellerError(error.response?.data?.detail || error.message);
    } finally {
      setContactSellerListingId("");
    }
  }

  async function reviewSavedListing(listing) {
    selectedListingIdRef.current = listing.id;
    setRatingError("");
    setIsRatingListing(true);

    let detail = listing;
    try {
      detail = await getMyListing({
        accessToken: auth.access_token,
        apiUrl: API_URL,
        listingId: listing.id,
      });
    } catch {
      // The review screen can still show the saved listing if detail reload fails.
    }

    setSelectedListing({
      ...detail,
      aiScore: detail.ai_score,
      inventoryCount: detail.inventory_count,
      image: salesListingImage(detail, 0),
      images: (detail.image_urls || [detail.primary_image_url]).filter(Boolean).map(mediaUrl),
      ownerId: auth?.user?.id,
      price: `${detail.price_amount} ${detail.price_currency}`,
      ratingAverage: auth?.user?.rating_average || 0,
      ratingCount: auth?.user?.rating_count || 0,
      seller: auth?.user?.display_name || "Dig",
      sellerRole: auth?.user?.role || "user",
      verified: auth?.user?.email_verified || false,
    });
    setSelectedListingRating(null);
    setSelectedListingImprovements(null);
    setDismissedImprovementKeys([]);
    setDetailReturnPage("sales");
    window.location.hash = hashForListing(detail.id);

    try {
      const rating = await rateListing({
        accessToken: auth.access_token,
        apiUrl: API_URL,
        listingId: listing.id,
      });
      if (selectedListingIdRef.current !== listing.id) return;
      const improvements = ratingImprovements(rating);
      setSelectedListingRating(rating);
      setSelectedListingImprovements(improvements);
      setDismissedImprovementKeys([]);
      setSelectedListing((current) =>
        current?.id === listing.id ? { ...current, aiScore: rating.score, ai_score: rating.score } : current
      );
      upsertMyListing({ ...detail, ai_score: rating.score });
    } catch (error) {
      setRatingError(error.response?.data?.detail || error.message);
    } finally {
      setIsRatingListing(false);
      await refreshMyListings();
    }
  }

  function handleDismissImprovement(suggestion) {
    const key = `${suggestion.field}-${suggestion.message}`;
    setDismissedImprovementKeys((current) =>
      current.includes(key) ? current : [...current, key]
    );
  }

  function handleSetPage(nextPage) {
    if (
      (nextPage === "search" ||
        nextPage === "profile" ||
        nextPage === "sales" ||
        nextPage === "shop" ||
        nextPage === "shop-create" ||
        nextPage === "shop-preview" ||
        nextPage === "shop-settings" ||
        nextPage === "shop-stock" ||
        nextPage === "messages" ||
        nextPage === "favorites" ||
        nextPage === "trades" ||
        nextPage === "sale-listings" ||
        nextPage === "browse-shops" ||
        nextPage === "admin-moderation" ||
        nextPage === "seller-profile" ||
        nextPage === "create-listing" ||
        nextPage === "edit-listing" ||
        nextPage === "listing-detail") &&
      !auth
    ) {
      setAuthNotice("Log ind for at fortsætte.");
      setRoutedPage("login");
      return;
    }

    setAuthNotice("");

    if (nextPage === "admin-moderation" && !auth?.user?.is_admin) {
      setRoutedPage("profile", { replace: true });
      return;
    }

    if (nextPage !== "trades") setTradeTarget(null);
    setRoutedPage(nextPage);
  }

  async function handleUpdateModerationFlag(flagId, status) {
    if (!auth?.access_token || !flagId) {
      return;
    }

    setUpdatingModerationItemKey(`flag:${flagId}`);
    setModerationFlagsError("");

    try {
      const updatedFlag = await updateModerationFlagStatus({
        accessToken: auth.access_token,
        apiUrl: API_URL,
        flagId,
        status,
      });
      setModerationFlags((current) =>
        current.map((flag) => (flag.id === flagId ? updatedFlag : flag))
      );
      await refreshModerationFlags();
    } catch (error) {
      setModerationFlagsError(error.response?.data?.detail || error.message);
    } finally {
      setUpdatingModerationItemKey("");
    }
  }

  async function handleUpdateListingReportStatus(reportId, status) {
    if (!auth?.access_token || !reportId) {
      return;
    }

    setUpdatingModerationItemKey(`listing-report:${reportId}`);
    setModerationFlagsError("");

    try {
      await updateListingReportStatus({
        accessToken: auth.access_token,
        apiUrl: API_URL,
        reportId,
        status,
      });
      await refreshModerationFlags();
    } catch (error) {
      setModerationFlagsError(error.response?.data?.detail || error.message);
    } finally {
      setUpdatingModerationItemKey("");
    }
  }

  async function handleUpdateMessageReportStatus(reportId, status) {
    if (!auth?.access_token || !reportId) {
      return;
    }

    setUpdatingModerationItemKey(`message-report:${reportId}`);
    setModerationFlagsError("");

    try {
      await updateMessageReportStatus({
        accessToken: auth.access_token,
        apiUrl: API_URL,
        reportId,
        status,
      });
      await refreshModerationFlags();
    } catch (error) {
      setModerationFlagsError(error.response?.data?.detail || error.message);
    } finally {
      setUpdatingModerationItemKey("");
    }
  }

  return (
    <main className="app-shell">
      <TopBar auth={auth} onLogout={handleLogout} page={page === "search" ? marketplaceNavPage(marketplaceTab) : page} setPage={handleSetPage} />

      <section className="workspace">
        {page === "search" && (
          auth ? (
            <MarketplacePage tab={marketplaceTab} query={listingSearchQuery} onQueryChange={setListingSearchQuery}
              category={activeCategory} onCategoryChange={setActiveCategory}
              shops={shopSearchResults} isLoadingShops={isLoadingShopSearch} shopError={shopSearchError}
              onOpenShop={handleOpenPublicShop}
              offersContent={<TradeOffers compact apiUrl={API_URL} auth={auth} onOpenConversation={openTradeConversation} />}
              tradeContent={<TradePage embedded apiUrl={API_URL} auth={auth}
                favoriteIds={favoriteIds} favoriteActionListingId={favoriteActionListingId}
                onToggleFavorite={handleToggleFavorite} favoriteError={favoritesError}
                searchQuery={listingSearchQuery} selectedCategory={activeCategory === "Alle" ? "" : activeCategory}
                initialListing={tradeTarget} onLogin={() => handleSetPage("login")}
                onCreateListing={createTradeListing} onOpenListing={listing => { handleOpenSearchListing(listing); setDetailReturnPage("trades"); }}
                onOpenConversation={openTradeConversation} />}>
            <SearchPage embedded
              activeCategory={activeCategory}
              handleFileChange={handleFileChange}
              handleSearch={handleSearch}
              hasSearched={hasSearched}
              browseError={browseListingsError}
              isLoadingBrowse={isLoadingBrowseListings}
              isSearching={isSearching}
              isLoadingShops={isLoadingShopSearch}
              searchQuery={listingSearchQuery}
              listings={filterMarketplaceListings(listings, marketplaceTab)}
              favoriteActionListingId={favoriteActionListingId}
              previewUrl={previewUrl}
              onOpenListing={handleOpenSearchListing}
              onOpenShop={handleOpenPublicShop}
              onClearSearch={handleClearSearch}
              onToggleFavorite={handleToggleFavorite}
              selectedFile={selectedFile}
              setActiveCategory={setActiveCategory}
              setSearchQuery={setListingSearchQuery}
              shopError={shopSearchError}
              shops={shopSearchResults.map((shop) => ({ ...shop, shop_logo_url: mediaUrl(shop.shop_logo_url) }))}
              status={status}
            />
            </MarketplacePage>
          ) : (
            <LoginPage
              email={loginEmail}
              error={loginError}
              notice="Log ind for at fortsætte."
              isSubmitting={isLoggingIn}
              onEmailChange={setLoginEmail}
              onLogin={handleLogin}
              onPasswordChange={setLoginPassword}
              password={loginPassword}
              switchToRegister={() => setRoutedPage("register")}
            />
          )
        )}

        {page === "sales" && (
          <SalesPage
            actionError={salesActionError}
            actionListingId={salesActionListingId}
            activeStatus={activeSalesStatus}
            error={myListingsError}
            isLoading={isLoadingMyListings}
            listings={myListings}
            onCreateListing={() => handleSetPage("create-listing")}
            onEditListing={handleEditListing}
            onListingImage={salesListingImage}
            onMarkSold={handleMarkSold}
            onOpenListing={handleOpenSalesListing}
            onOpenPublicShop={handleOpenPublicShop}
            onPublishListing={handlePublishListing}
            setActiveStatus={setActiveSalesStatus}
          />
        )}

        {(["shop", "shop-create", "shop-preview", "shop-settings", "shop-stock"].includes(page)) && (
          <ShopDashboardContainer
            auth={auth}
            listings={myListings}
            mediaUrl={mediaUrl}
            onListingDeleted={removeMyListing}
            onListingUpdated={upsertMyListing}
            onNavigate={handleSetPage}
            onOpenListing={handleOpenSalesListing}
            refreshListings={refreshMyListings}
            view={page === "shop-settings" ? "settings" : page === "shop-stock" ? "stock" : page === "shop-create" ? "create" : page === "shop-preview" ? "preview" : "dashboard"}
          />
        )}

        {page === "create-listing" && (
          <CreateListingPage
            draftConditionHints={editListingConditionHints}
            draftConfidence={editListingAiConfidence}
            draftFieldConfidences={editListingFieldConfidences}
            draftMissingInformation={editListingMissingInformation}
            draftQualityWarnings={editListingQualityWarnings}
            draftSuggestions={editListingAiSuggestions}
            draftTags={editListingTags}
            needsManualReview={editListingNeedsManualReview}
            error={createListingError}
            form={createListingForm}
            imagePreviewUrls={createListingPreviewUrls}
            isCreatingDraftFromImage={isCreatingDraftFromImage}
            isSubmitting={isCreatingListing}
            onCancel={() => {
              resetCreateListingForm();
              handleSetPage("sales");
            }}
            onChange={handleCreateListingChange}
            onCreateDraftFromImage={handleCreateDraftFromImage}
            onImageChange={handleCreateListingImageChange}
            onRemoveImage={handleRemoveCreateListingImage}
            onSubmit={handleCreateListing}
          />
        )}

        {page === "edit-listing" && (
          <CreateListingPage
            description="Opdater oplysningerne, eller tilføj nye billeder til annoncen."
            draftConditionHints={editListingConditionHints}
            draftConfidence={editListingAiConfidence}
            draftFieldConfidences={editListingFieldConfidences}
            draftMissingInformation={editListingMissingInformation}
            draftQualityWarnings={editListingQualityWarnings}
            draftSuggestions={editListingAiSuggestions}
            draftTags={editListingTags}
            needsManualReview={editListingNeedsManualReview}
            error={editListingError}
            form={editListingForm}
            heading="Rediger annonce"
            imagePreviewUrls={editListingPreviewUrls}
            isSubmitting={isEditingListing}
            onCancel={() => handleSetPage("sales")}
            onChange={handleEditListingChange}
            onImageChange={handleEditListingImageChange}
            onRemoveImage={editListingImage.length ? handleRemoveEditListingImage : undefined}
            onSubmit={handleEditListingSubmit}
            submitLabel="Gem ændringer"
            submittingLabel="Gemmer..."
          />
        )}

        {page === "listing-detail" && (
          <ListingDetailPage
            error={listingDetailError}
            isAuthenticated={Boolean(auth)}
            isLoading={isLoadingListingDetail}
            isOwner={selectedListing?.ownerId === auth?.user?.id}
            listing={selectedListing}
            onBack={handleBackFromDetail}
            onEdit={handleDetailEdit}
            onMarkSold={handleDetailMarkSold}
            onPublish={handleDetailPublish}
            onRate={handleRateListing}
            onContactSeller={handleContactSeller}
            onTrade={startTrade}
            onOpenSellerProfile={handleOpenSellerProfile}
            onReportListing={handleReportListing}
            onToggleFavorite={handleToggleFavorite}
            rating={selectedListingRating}
            ratingError={ratingError}
            isRating={isRatingListing}
            contactError={contactSellerError}
            isContacting={contactSellerListingId === selectedListing?.id}
            favoriteActionListingId={favoriteActionListingId}
            isReportingListing={reportingListingId === selectedListing?.id}
            listingReportError={listingReportError}
            listingReportNotice={listingReportNotice}
            improvements={selectedListingImprovements}
            dismissedImprovementKeys={dismissedImprovementKeys}
            onDismissImprovement={handleDismissImprovement}
          />
        )}

        {page === "seller-profile" && (
          <SellerProfilePage
            error={sellerProfileError}
            favoriteActionListingId={favoriteActionListingId}
            isLoading={isLoadingSellerProfile}
            onBack={handleBackFromSellerProfile}
            onOpenListing={handleOpenSellerListing}
            onToggleFavorite={handleToggleFavorite}
            profile={sellerProfile}
          />
        )}

        {page === "public-shop" && (
          <PublicShopPage
            actionError={publicShopActionError}
            auth={auth}
            error={publicShopError}
            isFollowing={publicShopFollowing}
            isLoading={isLoadingPublicShop}
            isUpdating={isUpdatingPublicShop}
            mediaUrl={mediaUrl}
            onBack={handleBackFromPublicShop}
            onOpenListing={handleOpenPublicShopListing}
            onReview={handleCreatePublicShopReview}
            onToggleFollow={handleTogglePublicShopFollow}
            reviews={publicShopReviews}
            shop={publicShop}
          />
        )}

        {page === "favorites" && (
          <FavoritesPage
            error={favoritesError}
            favoriteActionListingId={favoriteActionListingId}
            favorites={favoriteListings}
            isLoading={isLoadingFavorites}
            onOpenListing={handleOpenSearchListing}
            onToggleFavorite={handleToggleFavorite}
          />
        )}

        {page === "messages" && (
          <MessagesPage
            conversations={conversations}
            currentUserId={auth?.user?.id}
            detail={conversationDetail}
            error={messagesError}
            isLoading={isLoadingConversations}
            isLoadingDetail={isLoadingConversationDetail}
            isSending={isSendingMessage}
            messageDraft={messageDraft}
            onMessageDraftChange={setMessageDraft}
            onOpenConversation={openConversation}
            onReviewCommentChange={setSellerReviewComment}
            onReviewRatingChange={setSellerReviewRating}
            onReportMessage={handleReportMessage}
            onSendMessage={handleSendMessage}
            onSubmitSellerReview={handleCreateSellerReview}
            reportError={messageReportError}
            reportNotice={messageReportNotice}
            reportingMessageId={reportingMessageId}
            reviewComment={sellerReviewComment}
            reviewError={sellerReviewError}
            reviewNotice={sellerReviewNotice}
            reviewRating={sellerReviewRating}
            isSubmittingReview={isSubmittingSellerReview}
            selectedConversationId={selectedConversationId}
          />
        )}

        {page === "profile" && (
          <ProfilePage
            auth={auth}
            error={privateProfileError}
            isLoading={isLoadingPrivateProfile}
            isSaving={isSavingPrivateProfile}
            notice={privateProfileNotice}
            onSave={handleSavePrivateProfile}
            onUploadAvatar={handleUploadPrivateAvatar}
            profile={privateProfile}
          />
        )}

        {page === "admin-moderation" && (
          <AdminModerationPage
            activeStatus={moderationStatusFilter}
            activeTargetType={moderationTargetTypeFilter}
            error={moderationFlagsError}
            flags={moderationFlags}
            isLoading={isLoadingModerationFlags}
            listingReports={moderationListingReports}
            messageReports={moderationMessageReports}
            onBack={() => handleSetPage("profile")}
            onChangeStatusFilter={setModerationStatusFilter}
            onChangeTargetTypeFilter={setModerationTargetTypeFilter}
            onUpdateFlagStatus={handleUpdateModerationFlag}
            onUpdateListingReportStatus={handleUpdateListingReportStatus}
            onUpdateMessageReportStatus={handleUpdateMessageReportStatus}
            updatingItemKey={updatingModerationItemKey}
          />
        )}

        {page === "login" && (
          <LoginPage
            email={loginEmail}
            error={loginError}
            notice={authNotice}
            isSubmitting={isLoggingIn}
            onEmailChange={setLoginEmail}
            onLogin={handleLogin}
            onPasswordChange={setLoginPassword}
            password={loginPassword}
            switchToRegister={() => setRoutedPage("register")}
          />
        )}

        {page === "register" && (
          <RegisterPage
            displayName={registerDisplayName}
            email={registerEmail}
            error={registerError}
            isSubmitting={isRegistering}
            onDisplayNameChange={setRegisterDisplayName}
            onEmailChange={setRegisterEmail}
            onPasswordChange={setRegisterPassword}
            onRegister={handleRegister}
            password={registerPassword}
            switchToLogin={() => setRoutedPage("login")}
          />
        )}
      </section>

    </main>
  );
}

export default App;
